import axios, { AxiosError } from 'axios';
import type { AuthResponse, SSOInitiateResponse, TokenRefreshResponse } from './types';
import { AUTH_STORAGE_KEYS, API_ENDPOINTS } from './constants';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

interface SSOLoginPayload {
  access_token?: string;
  id_token?: string;
  code?: string;
}

class SSOService {
  async initiateSSOLogin(): Promise<string> {
    try {
      const response = await axios.get<SSOInitiateResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.SSO_INITIATE}`
      );
      return response.data.authorization_url;
    } catch (error) {
      console.error('SSO initiation failed:', error);
      throw new Error('Failed to initiate SSO login');
    }
  }

  async handleSSOLogin(
    accessToken?: string,
    idToken?: string,
    code?: string
  ): Promise<AuthResponse> {
    try {
      const payload: SSOLoginPayload = {};

      if (accessToken) {
        payload.access_token = accessToken;
      } else if (idToken) {
        payload.id_token = idToken;
      } else if (code) {
        payload.code = code;
      } else {
        throw new Error('Either access_token, id_token, or code must be provided');
      }

      const response = await axios.post<AuthResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.SSO_GOOGLE}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('SSO login failed:', error);

      const axiosError = error as AxiosError<{ detail?: string; non_field_errors?: string[] }>;

      if (axiosError.response?.status === 403) {
        throw new Error(
          axiosError.response.data.detail ||
            'Your email domain is not authorized for SSO. Please contact your administrator.'
        );
      }

      throw new Error(
        axiosError.response?.data?.non_field_errors?.[0] || 'SSO authentication failed'
      );
    }
  }

  storeTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  }

  clearTokens(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  }

  async refreshAccessToken(): Promise<string> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post<TokenRefreshResponse>(
        `${API_BASE_URL}${API_ENDPOINTS.TOKEN_REFRESH}`,
        { refresh: refreshToken }
      );

      const newAccessToken = response.data.access;
      this.storeTokens(newAccessToken, refreshToken);
      return newAccessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearTokens();
      throw new Error('Failed to refresh token');
    }
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }
}

export default new SSOService();

